import { AUTONOMY_LEVELS } from '../components/SystemIntakeFields';
import { useCallback, useMemo, useState } from 'react';
import {
  AI_ACTIVITIES,
  AI_ACTIVITY_LABELS,
  DATA_CLASSES,
  DATA_CLASS_LABELS,
  AFFECTED_PERSONS,
  AFFECTED_PERSON_LABELS,
} from '@aigovops/core';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
} from '@aigovops/design-system';
import { useOptionalWorkspace } from '../lib/workspace';
import { useWorkspaceQuery } from '../hooks/useWorkspaceQuery';
import { queryKeys } from '../lib/query-keys';
import {
  createUseCase,
  listUseCases,
  reviewUseCase,
  submitUseCase,
  type UseCaseRow,
} from '../lib/api';
import { fmtDate } from '../lib/format';

/**
 * /w/:slug/app/use-cases — AI use-case intake & review.
 *
 * Lists registered use cases with status + risk-tier badges, lets users
 * create new use cases via an inline form, submit drafts for review, and
 * approve/reject with a rationale modal.
 */

type StatusFilter = 'all' | 'draft' | 'submitted' | 'approved' | 'rejected';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];




const EXPOSURE_LEVELS = [
  { value: 'internal', label: 'Internal only' },
  { value: 'authenticated', label: 'Authenticated users' },
  { value: 'public', label: 'Public-facing' },
];

const STATUS_TONE: Record<string, 'accent' | 'neutral'> = {
  approved: 'accent',
  submitted: 'accent',
};

export function UseCaseIntake() {
  const workspaceCtx = useOptionalWorkspace();
  const slug = workspaceCtx?.workspace.slug ?? '';

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [purpose, setPurpose] = useState('');
  const [autonomy, setAutonomy] = useState(AUTONOMY_LEVELS[0].value);
  const [dataClasses, setDataClasses] = useState<string[]>([]);
  const [affectedPersons, setAffectedPersons] = useState<string[]>([]);
  const [aiActivities, setAiActivities] = useState<string[]>([]);
  const [exposureLevel, setExposureLevel] = useState('internal');

  // Review modal state
  const [reviewTarget, setReviewTarget] = useState<string | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'approve' | 'reject'>('approve');
  const [reviewRationale, setReviewRationale] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const list = useWorkspaceQuery(
    (slug) => queryKeys.useCases(slug, statusFilter === 'all' ? undefined : statusFilter),
    (slug) => listUseCases(slug, statusFilter === 'all' ? undefined : statusFilter),
  );

  const rows = useMemo(() => list.data?.use_cases ?? [], [list.data]);

  const resetForm = useCallback(() => {
    setTitle('');
    setDescription('');
    setPurpose('');
    setAutonomy(AUTONOMY_LEVELS[0].value);
    setDataClasses([]);
    setAffectedPersons([]);
    setAiActivities([]);
    setExposureLevel('internal');
    setFormError(null);
    setFormOpen(false);
  }, []);

  const handleCreate = useCallback(async () => {
    if (!slug || !title.trim() || !description.trim()) return;
    setFormBusy(true);
    setFormError(null);
    try {
      await createUseCase(slug, {
        title: title.trim(),
        description: description.trim(),
        purpose: purpose.trim(),
        autonomy_level: autonomy,
        data_classes: dataClasses.length > 0 ? dataClasses : undefined,
        affected_persons: affectedPersons.length > 0 ? affectedPersons : undefined,
        ai_activities: aiActivities.length > 0 ? aiActivities : undefined,
        deployment_context: { exposure_level: exposureLevel },
      });
      resetForm();
      list.refetch();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setFormBusy(false);
    }
  }, [slug, title, description, purpose, autonomy, dataClasses, affectedPersons, aiActivities, exposureLevel, resetForm, list]);

  const handleSubmit = useCallback(
    async (id: string) => {
      if (!slug) return;
      setActionError(null);
      try {
        await submitUseCase(slug, id);
        list.refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      }
    },
    [slug, list],
  );

  const handleReview = useCallback(async () => {
    if (!slug || !reviewTarget || !reviewRationale.trim()) return;
    setReviewBusy(true);
    setActionError(null);
    try {
      await reviewUseCase(slug, reviewTarget, {
        decision: reviewDecision,
        rationale: reviewRationale.trim(),
      });
      setReviewTarget(null);
      setReviewRationale('');
      list.refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setReviewBusy(false);
    }
  }, [slug, reviewTarget, reviewDecision, reviewRationale, list]);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Use-case intake"
        subtitle="Register AI use cases for classification and governance review."
        right={
          !formOpen ? (
            <Button variant="primary" compact onClick={() => setFormOpen(true)}>
              + New use case
            </Button>
          ) : null
        }
      />

      {actionError ? <Alert variant="error">{actionError}</Alert> : null}

      {formOpen ? (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            background: 'var(--surface)',
            padding: 'var(--space-4)',
            display: 'grid',
            gap: 'var(--space-3)',
          }}
        >
          <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
            New use case
          </div>
          <Field id="uc-title" label="Title">
            <input
              id="uc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Customer support chatbot"
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
              }}
            />
          </Field>
          <Field id="uc-desc" label="Description">
            <textarea
              id="uc-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this AI system do?"
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
                resize: 'vertical',
              }}
            />
          </Field>
          <Field id="uc-purpose" label="Purpose">
            <input
              id="uc-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Business objective this system supports"
              style={{
                width: '100%',
                padding: 'var(--space-2) var(--space-3)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-input, var(--radius-card))',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontSize: 'var(--font-size-2xs)',
              }}
            />
          </Field>
          <Field id="uc-autonomy" label="Autonomy level">
            <Select
              id="uc-autonomy"
              value={autonomy}
              onChange={(e) => setAutonomy(e.target.value)}
            >
              {AUTONOMY_LEVELS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </Select>
          </Field>
          <Field id="uc-data-classes" label="Data classes processed">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {DATA_CLASSES.map((dc) => (
                <label key={dc} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={dataClasses.includes(dc)}
                    onChange={(e) => {
                      setDataClasses((prev) =>
                        e.target.checked ? [...prev, dc] : prev.filter((v) => v !== dc)
                      );
                    }}
                  />
                  {DATA_CLASS_LABELS[dc]}
                </label>
              ))}
            </div>
          </Field>
          <Field id="uc-affected" label="Affected persons">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {AFFECTED_PERSONS.map((ap) => (
                <label key={ap} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={affectedPersons.includes(ap)}
                    onChange={(e) => {
                      setAffectedPersons((prev) =>
                        e.target.checked ? [...prev, ap] : prev.filter((v) => v !== ap)
                      );
                    }}
                  />
                  {AFFECTED_PERSON_LABELS[ap]}
                </label>
              ))}
            </div>
          </Field>
          <Field id="uc-exposure" label="Exposure level">
            <Select
              id="uc-exposure"
              value={exposureLevel}
              onChange={(e) => setExposureLevel(e.target.value)}
            >
              {EXPOSURE_LEVELS.map((el) => (
                <option key={el.value} value={el.value}>{el.label}</option>
              ))}
            </Select>
          </Field>
          <Field id="uc-activities" label="AI activities (NIST AI 200-1)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {AI_ACTIVITIES.map((act) => (
                <label key={act} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--font-size-2xs)', color: 'var(--text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={aiActivities.includes(act)}
                    onChange={(e) => {
                      setAiActivities((prev) =>
                        e.target.checked ? [...prev, act] : prev.filter((v) => v !== act)
                      );
                    }}
                  />
                  {AI_ACTIVITY_LABELS[act]}
                </label>
              ))}
            </div>
          </Field>
          {formError ? <Alert variant="error">{formError}</Alert> : null}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="primary"
              compact
              disabled={formBusy || !title.trim() || !description.trim()}
              onClick={handleCreate}
            >
              {formBusy ? 'Creating...' : 'Create use case'}
            </Button>
            <Button variant="ghost" compact disabled={formBusy} onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'var(--surface)',
        }}
      >
        <Field id="uc-filter" label="Status">
          <Select
            id="uc-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {list.loading ? (
        <Spinner label="Loading use cases" />
      ) : list.error ? (
        <Alert variant="error">{list.error}</Alert>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No use cases yet"
          description="Register your first AI use case to begin the classification and governance workflow."
          action={
            !formOpen ? (
              <Button variant="primary" compact onClick={() => setFormOpen(true)}>
                + New use case
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {rows.map((uc) => (
            <UseCaseRow
              key={uc.use_case_id}
              useCase={uc}
              onSubmit={() => handleSubmit(uc.use_case_id)}
              onOpenReview={(decision) => {
                setReviewTarget(uc.use_case_id);
                setReviewDecision(decision);
                setReviewRationale('');
              }}
            />
          ))}
        </ol>
      )}

      {/* Review rationale modal */}
      {reviewTarget ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 100,
          }}
          onClick={() => { if (!reviewBusy) setReviewTarget(null); }}
        >
          <div
            role="dialog"
            aria-label={`${reviewDecision === 'approve' ? 'Approve' : 'Reject'} use case`}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface-elevated)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              padding: 'var(--space-5)',
              width: '100%',
              maxWidth: '480px',
              display: 'grid',
              gap: 'var(--space-3)',
            }}
          >
            <div style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
              {reviewDecision === 'approve' ? 'Approve' : 'Reject'} use case
            </div>
            <Field id="review-rationale" label="Rationale">
              <textarea
                id="review-rationale"
                rows={3}
                value={reviewRationale}
                onChange={(e) => setReviewRationale(e.target.value)}
                placeholder="Explain your decision..."
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-input, var(--radius-card))',
                  background: 'var(--surface)',
                  color: 'var(--text)',
                  fontSize: 'var(--font-size-2xs)',
                  resize: 'vertical',
                }}
              />
            </Field>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <Button variant="ghost" compact disabled={reviewBusy} onClick={() => setReviewTarget(null)}>
                Cancel
              </Button>
              <Button
                variant={reviewDecision === 'approve' ? 'primary' : 'danger'}
                compact
                disabled={reviewBusy || !reviewRationale.trim()}
                onClick={handleReview}
              >
                {reviewBusy ? 'Working...' : reviewDecision === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function UseCaseRow({
  useCase,
  onSubmit,
  onOpenReview,
}: {
  useCase: UseCaseRow;
  onSubmit: () => void;
  onOpenReview: (decision: 'approve' | 'reject') => void;
}) {
  const uc = useCase;
  return (
    <li
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ display: 'grid', gap: '4px', minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, color: 'var(--text)' }}>
              {uc.title}
            </span>
            <Badge tone={STATUS_TONE[uc.status] ?? 'neutral'}>{uc.status}</Badge>
            {uc.risk_tier ? (
              <span
                style={{
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-chip)',
                  border: '1px solid var(--border)',
                  background: 'var(--surface-elevated, var(--surface))',
                  color: 'var(--text-muted)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                }}
              >
                {uc.risk_tier}
              </span>
            ) : null}
          </div>
          <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
            {uc.description}
          </p>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            Autonomy: {uc.autonomy_level.replace(/_/g, ' ')}
            {uc.eu_ai_act_tier ? ` · EU AI Act: ${uc.eu_ai_act_tier}` : ''}
            {' · '}Created {fmtDate(uc.created_at)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexShrink: 0 }}>
          {uc.status === 'draft' ? (
            <Button variant="secondary" compact onClick={onSubmit}>Submit</Button>
          ) : null}
          {uc.status === 'submitted' ? (
            <>
              <Button variant="primary" compact onClick={() => onOpenReview('approve')}>Approve</Button>
              <Button variant="danger" compact onClick={() => onOpenReview('reject')}>Reject</Button>
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}
